import { getNthColor } from "./helpers/colors";
import { STATE_CODES, normalizeUsStateCode } from "./states";
import { FinancialAccountType } from "./types/financialAccount";
import { ConnectionProviderType } from "./types/financialConnection";
import { OrganizationUserRole } from "./userOrganizationRoles";
import { TransactionClassification } from "./types/transaction";
import { UserWorkspaceRole } from "./userWorkspaceRoles";

export const TRANSACTION_STATUS_LABELS = {
    pending: "Pending",
    completed: "Success",
    failed: "Failed",
};

export const KICK_ADMIN_OPS_DESCRIPTION = "Admin <admin@kick.co>";

export const KICK_TRANSACTION_DESCRIPTION = "Kick";

export const PHONE_NUMBER_PREFIX = "+1";

export const FINANCIAL_ACCOUNT_TYPE_LABEL: Record<
    FinancialAccountType,
    string
> = {
    [FinancialAccountType.CHECKING]: "Checking",
    [FinancialAccountType.CREDIT_CARD]: "Credit card",
    [FinancialAccountType.SAVINGS]: "Savings",
    [FinancialAccountType.LOAN]: "Loan",
    [FinancialAccountType.LINE_OF_CREDIT]: "Line of credit",
    [FinancialAccountType.INVESTMENT]: "Investment",
    [FinancialAccountType.PAYMENT_PROCESSOR]: "Payment processor",
    [FinancialAccountType.OTHER]: "Other",
    [FinancialAccountType.CASH]: "Cash",
};

export const ALLOWED_FINANCIAL_ACCOUNT_TYPES_FOR_FILE_IMPORT = [
    FinancialAccountType.CHECKING,
    FinancialAccountType.SAVINGS,
    FinancialAccountType.CREDIT_CARD,
    FinancialAccountType.LINE_OF_CREDIT,
    FinancialAccountType.PAYMENT_PROCESSOR,
];

export const ALLOWED_FILE_TYPES_FOR_FINANCIAL_ACCOUNTS = ["text/csv"];

export enum OAuthServiceType {
    QUICKBOOKS = "quickbooks",
    STRIPE = "stripe",
    GUSTO = "gusto",
    MERCURY = "mercury",
    RAMP = "ramp",
    GOOGLE = "google",
    GMAIL = "gmail",
    ADP_RUN = "adp_run",
}

export const SENTRY_SERVICE_TAG = "service";
export const TWILIO_CLIENT = Symbol("TWILIO_CLIENT");
export const PLAID_CLIENT = Symbol("PLAID_CLIENT");
export const PLAID_PARTNER_CLIENT = Symbol("PLAID_PARTNER_CLIENT");
export const PDF_GENERATION_CLIENT = Symbol("PDF_GENERATION_CLIENT");
export const STRIPE_CLIENT = Symbol("STRIPE_CLIENT");
export const FLAT_RATE_BILLING_CONFIG = Symbol("FLAT_RATE_BILLING_CONFIG");

export const MAX_RECEIPT_SIZE_BYTES = 20 * 1024 * 1024;

export const TRANSACTION_CLASSIFICATION_LABELS: Record<
    TransactionClassification,
    string
> = {
    [TransactionClassification.BUSINESS]: "Business",
    [TransactionClassification.PERSONAL]: "Personal",
};

export enum CustomLocationOption {
    INTERNATIONAL = "international",
    UNKNOWN = "unknown",
}

export const TAX_LOCATIONS = [
    ...Object.values(CustomLocationOption),
    ...STATE_CODES,
];

const CUSTOM_LOCATION_BY_NORMALIZED_LABEL = new Map<string, string>(
    Object.values(CustomLocationOption).map((option) => [
        option.toLowerCase(),
        option,
    ]),
);

// A tax location is either a US state (accept code or full name) or one of the
// custom options. Normalize agent input ("California", "ca", "International") to
// the canonical value the tax_location enum stores. Returns null when invalid.
export function normalizeTaxLocation(value: string): string | null {
    return (
        CUSTOM_LOCATION_BY_NORMALIZED_LABEL.get(value.trim().toLowerCase()) ??
        normalizeUsStateCode(value)
    );
}

export enum FinancialAccountClassification {
    BUSINESS = "business",
    MIXED = "mixed",
}

export enum PlaidConnectionErrors {
    DISCONNECTED = "disconnected",
    MFA_REQUIRED = "mfa_required",
    ITEM_NOT_SUPPORTED = "item_not_supported",
    ITEM_REMOVED = "item_removed",
}

export const WORKSPACE_ROLES_LABELS: Record<UserWorkspaceRole, string> = {
    [UserWorkspaceRole.PRIMARY_ADMIN]: "Primary Admin",
    [UserWorkspaceRole.ADMIN]: "Admin",
    [UserWorkspaceRole.MEMBER]: "Member",
    [UserWorkspaceRole.ACCOUNTANT]: "Accountant",
    [UserWorkspaceRole.VIEW_ONLY]: "View-only",
};

export const ORGANIZATION_ROLES_LABELS: Record<OrganizationUserRole, string> = {
    [OrganizationUserRole.OWNER]: "Owner",
    [OrganizationUserRole.ADMIN]: "Admin",
    [OrganizationUserRole.MANAGER]: "Manager",
    [OrganizationUserRole.MEMBER]: "Member",
};

export const WORKSPACE_ADMIN_ROLES = [
    UserWorkspaceRole.PRIMARY_ADMIN,
    UserWorkspaceRole.ADMIN,
];

export const VERSION_HEADER = "x-app-version" as const;

export const PERSONAL_ENTITY_ICON_COLOR = "#D9DDE2";
export const OTHER_ENTITY_COLOR = getNthColor(2);

export const EMPTY_STATE = "-";

export const UNKNOWN_AUDIT_LOG_AUTHOR = "Unknown";

export enum FIELD_UPDATE_SOURCE {
    // Data sources
    PLAID_IMPORT = "plaid_import",
    FILE_IMPORT = "file_import",
    STRIPE = "stripe",
    MERCURY = "mercury",
    RAMP = "ramp",
    PAYPAL = "paypal",
    GUSTO = "gusto",
    BILLCOM = "billcom",
    ADP_RUN = "adp_run",

    // Kick app functionality
    CATEGORIZATION_MODEL_TIER_1 = "categorizationModelTier1",
    COUNTERPARTY_ENRICHMENT = "counterpartyEnrichment",
    COUNTERPARTY_ENRICHMENT_NORMALIZATION = "counterpartyEnrichmentNormalization",
    CATEGORIZATION_NORMALIZATION = "categorizationNormalization",
    CATEGORIZATION_RULE_CREATED = "categorizationRuleCreated",
    CATEGORIZATION_RULE_TRIGGERED = "categorizationRuleTriggered",
    CATEGORIZATION_RULE_REVERSED = "categorizationRuleReversed",
    DESCRIPTION_NORMALIZATION = "descriptionNormalization",
    DESCRIPTION_CROSS_NORMALIZATION = "descriptionCrossNormalization",
    COUNTERPARTY_CROSS_NORMALIZATION = "counterpartyCrossNormalization",
    COUNTERPARTY_DEDUPLICATION = "counterpartyDeduplication",
    MANUAL_TRANSFER_MATCHING = "manualTransferMatching",
    MANUAL_TRANSFER_UNMATCHING = "manualTransferUnmatching",
    AUTO_TRANSFER_MATCHING = "autoTransferMatching",
    AUTO_TRANSFER_UNMATCHING = "autoTransferUnmatching",
    AUTO_INVOICE_MATCHING = "autoInvoiceMatching",
    AUTO_RECEIPT_MATCHING = "autoReceiptMatching",
    AUTO_BILL_MATCHING = "autoBillMatching",
    MANUAL_RECEIPT_MATCHING = "manualReceiptMatching",
    TRANSACTION_FEED = "transactionFeed",
    ACTIONS = "actions",
    CUSTOM_CATEGORY_REMOVAL = "customCategoryRemoval",
    DIRECTORY = "directory",
    RECONCILIATION = "reconciliation",
    LOAN_RECONCILIATION = "loan_reconciliation",
    // NOTE: waiting for complete removal of this functionality before dropping
    SIMILARITY = "similarity",

    // NOTE: any update from admin app
    ADMIN = "admin",

    // NOTE: updates coming through the external Platform API, authenticated
    // as an organization access token rather than a user
    PLATFORM_API = "platformApi",

    // NOTE: everything else ie. report generating lambda
    SYSTEM = "system",

    ACCRUAL_PARTY_MATCHING = "accrualPartyMatching",
    ACCRUAL_DEFAULT_ACCOUNTS = "accrualDefaultAccounts",
}

// Integration sources that set category of a transaction
// Including an integration here means that the category won't be overridden
// by ML, but can still be overridden by users or rules
export const INTEGRATION_FIELD_UPDATE_SOURCES: FIELD_UPDATE_SOURCE[] = [
    FIELD_UPDATE_SOURCE.STRIPE,
    FIELD_UPDATE_SOURCE.MERCURY,
    FIELD_UPDATE_SOURCE.RAMP,
    FIELD_UPDATE_SOURCE.PAYPAL,
    FIELD_UPDATE_SOURCE.BILLCOM,
];

// NOTE: other sources are supposed to be automatically triggered and
// therefore userId either doesn't make sense (e.g. for CATEGORIZATION_MODEL_TIER_1)
// OR can be extracted from another entity based on metadata (e.g. ruleId => rule.userId)
export type FIELD_UPDATE_SOURCES_REQUIRING_USER_OR_OPS =
    | FIELD_UPDATE_SOURCE.ADMIN
    | FIELD_UPDATE_SOURCE.MANUAL_TRANSFER_MATCHING
    | FIELD_UPDATE_SOURCE.MANUAL_TRANSFER_UNMATCHING
    | FIELD_UPDATE_SOURCE.MANUAL_RECEIPT_MATCHING
    // | FIELD_UPDATE_SOURCE.FILE_IMPORT
    | FIELD_UPDATE_SOURCE.ACTIONS
    | FIELD_UPDATE_SOURCE.TRANSACTION_FEED;
export const FIELD_UPDATE_SOURCES_VALUES_REQUIRING_USER_OR_OPS: FIELD_UPDATE_SOURCES_REQUIRING_USER_OR_OPS[] =
    [
        FIELD_UPDATE_SOURCE.ADMIN,
        FIELD_UPDATE_SOURCE.MANUAL_TRANSFER_MATCHING,
        FIELD_UPDATE_SOURCE.MANUAL_TRANSFER_UNMATCHING,
        FIELD_UPDATE_SOURCE.MANUAL_RECEIPT_MATCHING,
        // FIELD_UPDATE_SOURCE.FILE_IMPORT,
        FIELD_UPDATE_SOURCE.ACTIONS,
        FIELD_UPDATE_SOURCE.TRANSACTION_FEED,
    ];

// Sources that own the account they wrote: user-facing surfaces, plus the
// document-matching flows that pick AP / AR accounts for accounting reasons.
// Accounting rules never overwrite an account written by one of these. An
// account written by any other source, or with no recorded source, is owned
// by automation and stays open for accounting rules to re-decide.
export const RULE_PROTECTED_ACCOUNT_SOURCES: FIELD_UPDATE_SOURCE[] = [
    ...FIELD_UPDATE_SOURCES_VALUES_REQUIRING_USER_OR_OPS,
    FIELD_UPDATE_SOURCE.DIRECTORY,
    FIELD_UPDATE_SOURCE.RECONCILIATION,
    FIELD_UPDATE_SOURCE.PLATFORM_API,
    FIELD_UPDATE_SOURCE.LOAN_RECONCILIATION,
    FIELD_UPDATE_SOURCE.AUTO_BILL_MATCHING,
    FIELD_UPDATE_SOURCE.AUTO_INVOICE_MATCHING,
    FIELD_UPDATE_SOURCE.AUTO_TRANSFER_MATCHING,
    FIELD_UPDATE_SOURCE.ACCRUAL_PARTY_MATCHING,
];

export enum MoneyDirection {
    MONEY_IN = "MONEY_IN",
    MONEY_OUT = "MONEY_OUT",
}

export enum SortOrder {
    ASC = "ASC",
    DESC = "DESC",
}

export enum NullsOrders {
    FIRST = "NULLS FIRST",
    LAST = "NULLS LAST",
}

export const DatesComparisonOperands = {
    since: ">=",
    until: "<",
};

export const institutionsWithManuallyProvidedData = [
    "ins_128026", // Capital One
];
export const REPORTS_CONTROLLER_DATE_FORMAT = "yyyy-MM-dd";

export enum AccountingReportCycle {
    WEEK = "week",
    MONTH = "month",
    QUARTER = "quarter",
    YEAR = "year",
    ENTITY = "entity",
    CLASS = "class",
    SUBCLASS = "subclass",
}

export type TimeBasedAccountingReportCycle = Extract<
    AccountingReportCycle,
    | AccountingReportCycle.WEEK
    | AccountingReportCycle.MONTH
    | AccountingReportCycle.QUARTER
    | AccountingReportCycle.YEAR
>;

export type ClassBasedCycle =
    | AccountingReportCycle.CLASS
    | AccountingReportCycle.SUBCLASS;

export enum AccountingReportCycleTotal {
    TOTAL = "total",
}
export type AccountingReportCycleOptionType =
    | AccountingReportCycle
    | AccountingReportCycleTotal;

export enum AccountingReportComparison {
    PREVIOUS_YEAR = "previous-year",
    PREVIOUS_PERIOD = "previous-period",
}

export enum AccountingReportComparisonDiff {
    PERCENTAGE = "percentage",
    ABSOLUTE = "absolute",
}

export enum AccountingReportRatioType {
    INCOME_PERCENTAGE = "income-percentage",
    EXPENSE_PERCENTAGE = "expense-percentage",
}

export const MULTI_ENTITY_CROSS_WORKSPACE_ERROR =
    "All entities must belong to the same workspace.";

export const NO_LEDGER_FOR_ENTITY_ERROR =
    "One or more selected entities have no accounting ledger for the requested basis.";

export const DEFAULT_NUMBER_OF_MONTHS_AVAILABLE = 3;

export const TRANSACTION_MATERIALITY_THRESHOLD = 0.02;

export enum UpdateEmailsFrequency {
    WEEK = "week",
    MONTH = "month",
}

export enum SERVICE_PREFIX {
    MAIN_APP = "api",
    ADMIN = "admin2",
}

export const FinancialConnectionProviderLabel: Record<
    ConnectionProviderType,
    string
> = {
    [ConnectionProviderType.PLAID]: "Plaid",
    [ConnectionProviderType.STRIPE]: "Stripe",
    [ConnectionProviderType.GUSTO]: "Gusto",
    [ConnectionProviderType.MERCURY]: "Mercury",
    [ConnectionProviderType.RAMP]: "Ramp",
};

export enum TransactionLocked {
    LOCKED = "locked",
    UNLOCKED = "unlocked",
}

export enum FinancialDocumentBulkActionTypes {
    REMOVE = "REMOVE",
    ASSIGN_ENTITY = "ASSIGN_ENTITY",
    EDIT_DOCUMENT_CATEGORY = "EDIT_DOCUMENT_CATEGORY",
    ADD_TO_IMPORTER = "ADD_TO_IMPORTER",
    UNMATCH_TRANSACTIONS = "UNMATCH_TRANSACTIONS",
    ASSIGN_FINANCIAL_ACCOUNT = "ASSIGN_FINANCIAL_ACCOUNT",
    ASSIGN_COUNTERPARTY = "ASSIGN_COUNTERPARTY",
    REVERT_RENAME = "REVERT_RENAME",
}

export const GOOGLE_AUTH_REDIRECT_PATH = "/google";

export const MERCURY_API_URL = "https://api.mercury.com/api/v1";

export const MERCURY_OAUTH_URL = "https://oauth2.mercury.com";
export const ADP_ACCOUNTS_URL = "https://accounts.adp.com";
export const ADP_API_URL = "https://api.adp.com";
export const AWS_REGION = "us-west-2";

export enum PLAID_INSTITUTIONS_NAMES {
    CHASE = "chase",
    BANK_OF_AMERICA = "bankofamerica",
    WELLS_FARGO = "wellsfargo",
    CITIBANK = "citibank",
    CAPITAL_ONE = "capitalone",
    AMERICAN_EXPRESS = "americanexpress",
    RELAY = "relay",
}

// Same value as ai/src/domain/embedding.py:CURRENT_ENGINE_VERSION
export const CURRENT_COUNTERPARTY_ENGINE_VERSION = 2;
export const DEFAULT_COUNTERPARTY_ENGINE_VERSION = 1;

export const EMPTY_CELL_VALUE = " - ";

export const STRIPE_API_KEY_PLACEHOLDER = "stripe_api_key_placeholder";
